import { useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { Tab, Tabs } from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PageHeader from '../components/PageHeader';
import TabHeader from '../components/TabHeader';
import MemberRow from '../features/settings/components/users/MemberRow';
import InviteUserModal from '../features/settings/components/users/InviteUserModal';
import EditRoleModal from '../features/settings/components/users/EditRoleModal';
import { useOrgMembers } from '../features/settings/hooks/useOrgMembers';
import { useNotification } from '../context/useNotification';
import { USERS_TAB_LABELS as LABELS, INVITE_MODAL_LABELS } from '../utils/labels';
import { hasFullAccess, isLastFullAccessMember } from '../utils/roles';
import type { MemberRole } from '../types/roles';
import type { OrgMember } from '../features/settings/types';

interface UsersPageProps {
  session: Session;
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

export default function UsersPage({ session, orgId, isSuperAdmin, role }: UsersPageProps) {
  const { activeMembers, deactivatedMembers, inviteMember, deactivateMember, reactivateMember, updateMemberRole } =
    useOrgMembers(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const canEdit = hasFullAccess(role, isSuperAdmin);
  const [tab, setTab] = useState<'active' | 'deactivated'>('active');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<OrgMember | null>(null);

  const list = tab === 'active' ? activeMembers : deactivatedMembers;

  const handleDeactivate = async (userId: string) => {
    if (isLastFullAccessMember(activeMembers, userId)) {
      notifyError(LABELS.lastFullAccessDeactivateBlocked);
      return;
    }
    const ok = await deactivateMember(userId);
    if (ok) notifySuccess(LABELS.deactivateSuccess); else notifyError(LABELS.mutationError);
  };

  const handleReactivate = async (userId: string) => {
    const ok = await reactivateMember(userId);
    if (ok) notifySuccess(LABELS.reactivateSuccess); else notifyError(LABELS.mutationError);
  };

  const handleInvite = async (email: string, firstName: string, lastName: string, inviteRole: MemberRole) => {
    const result = await inviteMember(email, firstName, lastName, inviteRole);
    if (result === 'ok') notifySuccess(INVITE_MODAL_LABELS.inviteSuccess); else notifyError(INVITE_MODAL_LABELS.inviteError);
    return result;
  };

  const handleRoleSave = async (newRole: MemberRole): Promise<boolean> => {
    if (!editingMember) return false;
    if (newRole !== 'full_access' && isLastFullAccessMember(activeMembers, editingMember.user_id)) {
      notifyError(LABELS.lastFullAccessRoleBlocked);
      return false;
    }
    const ok = await updateMemberRole(editingMember.user_id, newRole);
    if (ok) notifySuccess(LABELS.roleUpdateSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={canEdit ? LABELS.inviteButton : undefined}
            onAdd={canEdit ? () => setInviteOpen(true) : undefined}
            addIcon={<PersonAddOutlinedIcon />}
          />

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              className="border-b border-border px-2"
            >
              <Tab label={LABELS.tabActive} value="active" />
              <Tab label={LABELS.tabDeactivated} value="deactivated" />
            </Tabs>

            {list.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {tab === 'active' ? LABELS.noActive : LABELS.noDeactivated}
              </p>
            ) : (
              list.map((m, i) => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  isSelf={m.user_id === session.user.id}
                  showDivider={i > 0}
                  showRole={canEdit}
                  onDeactivate={canEdit && tab === 'active' ? () => handleDeactivate(m.user_id) : undefined}
                  onReactivate={canEdit && tab === 'deactivated' ? () => handleReactivate(m.user_id) : undefined}
                  onEditRole={canEdit && tab === 'active' ? () => setEditingMember(m) : undefined}
                />
              ))
            )}
          </div>

          {inviteOpen && (
            <InviteUserModal onClose={() => setInviteOpen(false)} onInvite={handleInvite} />
          )}

          {editingMember && (
            <EditRoleModal
              member={editingMember}
              onClose={() => setEditingMember(null)}
              onSave={handleRoleSave}
            />
          )}
        </div>
      </main>
    </div>
  );
}
